// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import type { StepComponentProps } from '../../types/wizard';
import { POLICY_TYPE_KEYS, POLICY_TYPE_LABELS } from '../../types/wizard';
import type { PolicyTypeKey } from '../../types/wizard';
import { DateInput } from '../wizard/DateInput';
import { CurrencyInput } from '../wizard/CurrencyInput';

const STATUS_OPTIONS = [
  { value: '', text: 'Select status...' },
  { value: 'ACTIVE', text: 'Active' },
  { value: 'CLOSED', text: 'Closed' },
];

const CLOSED_REASON_OPTIONS = [
  { value: '', text: 'Select reason...' },
  { value: 'RETIRED', text: 'Retired' },
  { value: 'TRANSFERRED_OUT', text: 'Transferred Out' },
];

const RETIRED_POLICY_TYPE_OPTIONS = [
  { value: '', text: 'Select type...' },
  { value: 'ARF', text: 'ARF' },
  { value: 'VESTED_PRSA', text: 'Vested PRSA' },
  { value: 'OVERSEAS_TRANSFER', text: 'Overseas Transfer' },
];

const TRANSFERRED_POLICY_TYPE_OPTIONS = [
  { value: '', text: 'Select type...' },
  ...POLICY_TYPE_KEYS.map((key) => ({
    value: key,
    text: POLICY_TYPE_LABELS[key as PolicyTypeKey],
  })),
  { value: 'OVERSEAS_TRANSFER', text: 'Overseas Transfer' },
];

const TRANSFER_TYPE_OPTIONS = [
  { value: '', text: 'Select...' },
  { value: 'CASH', text: 'Cash' },
  { value: 'IN_SPECIE', text: 'In-specie' },
];

const inputClass = "w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent";
const labelClass = "block text-xs font-medium text-gray-700 mb-1";

export function StepPolicyStatus({
  data,
  onFieldChange,
  fieldErrors,
  onClearFieldError,
}: StepComponentProps) {
  const status = data.policyStatus as string;
  const isClosed = status === 'CLOSED';
  const closedReason = data.policyClosedReason as string;
  const isRetired = isClosed && closedReason === 'RETIRED';
  const isTransferred = isClosed && closedReason === 'TRANSFERRED_OUT';

  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-lg font-semibold text-gray-900">Policy status</h2>

      <div>
        <label className={labelClass}>
          Policy status <span className="text-red-500">*</span>
        </label>
        <select
          value={status || ''}
          onChange={(e) => {
            onFieldChange('policyStatus', e.target.value);
            onClearFieldError('policyStatus');
            // Clear sub-fields when status changes
            if (e.target.value !== 'CLOSED') {
              onFieldChange('policyClosedReason', undefined);
            }
          }}
          className={`${inputClass} ${fieldErrors.policyStatus ? 'border-red-400' : ''}`}
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.text}</option>
          ))}
        </select>
        {fieldErrors.policyStatus && (
          <p className="text-xs text-red-500 mt-1">{fieldErrors.policyStatus}</p>
        )}
      </div>

      {isClosed && (
        <>
          <div>
            <label className={labelClass}>
              Reason for closure <span className="text-red-500">*</span>
            </label>
            <select
              value={closedReason || ''}
              onChange={(e) => {
                onFieldChange('policyClosedReason', e.target.value);
                onClearFieldError('policyClosedReason');
              }}
              className={`${inputClass} ${fieldErrors.policyClosedReason ? 'border-red-400' : ''}`}
            >
              {CLOSED_REASON_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.text}</option>
              ))}
            </select>
            {fieldErrors.policyClosedReason && (
              <p className="text-xs text-red-500 mt-1">{fieldErrors.policyClosedReason}</p>
            )}
          </div>

          {isRetired && (
            <div className="rounded-xl border border-gray-200 bg-white p-5">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Retired details</h4>

              <div className="flex flex-col gap-4">
                <div>
                  <label className={labelClass}>Provider name (retired to)</label>
                  <input
                    placeholder="Provider name"
                    value={(data.retiredProviderName as string) || ''}
                    onChange={(e) => {
                      onFieldChange('retiredProviderName', e.target.value);
                    }}
                    className={inputClass}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <DateInput
                    label="Date of transfer"
                    value={(data.retiredDateOfTransfer as string) || ''}
                    onChange={(value) => {
                      onFieldChange('retiredDateOfTransfer', value);
                    }}
                  />
                  <div>
                    <label className={labelClass}>Policy reference number (retired to)</label>
                    <input
                      placeholder="Reference no."
                      value={(data.retiredPolicyReference as string) || ''}
                      onChange={(e) => {
                        onFieldChange('retiredPolicyReference', e.target.value);
                      }}
                      className={inputClass}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>Type of policy (retired to)</label>
                    <select
                      value={(data.retiredPolicyType as string) || ''}
                      onChange={(e) => {
                        onFieldChange('retiredPolicyType', e.target.value);
                      }}
                      className={inputClass}
                    >
                      {RETIRED_POLICY_TYPE_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.text}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>Type of transfer</label>
                    <select
                      value={(data.retiredTransferType as string) || ''}
                      onChange={(e) => {
                        onFieldChange('retiredTransferType', e.target.value);
                      }}
                      className={inputClass}
                    >
                      {TRANSFER_TYPE_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.text}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <CurrencyInput
                  label="Value as at transfer date"
                  value={data.retiredTransferValue as number | undefined}
                  onChange={(value) => {
                    onFieldChange('retiredTransferValue', value ? parseFloat(value) : undefined);
                  }}
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <DateInput
                    label="Date of Benefit Crystallisation Event"
                    value={(data.retiredBceDate as string) || ''}
                    onChange={(value) => {
                      onFieldChange('retiredBceDate', value);
                    }}
                  />
                  <CurrencyInput
                    label="Value of policy as a BCE"
                    value={data.retiredBceValue as number | undefined}
                    onChange={(value) => {
                      onFieldChange('retiredBceValue', value ? parseFloat(value) : undefined);
                    }}
                  />
                </div>

                <CurrencyInput
                  label="Tax-free lump sum"
                  value={data.retiredTaxFreeLumpSum as number | undefined}
                  onChange={(value) => {
                    onFieldChange('retiredTaxFreeLumpSum', value ? parseFloat(value) : undefined);
                  }}
                />

                <div>
                  <label className={labelClass}>Details of previous tax-free lump sums</label>
                  <textarea
                    placeholder="Amounts and dates of previous tax-free lump sums..."
                    maxLength={2000}
                    value={(data.retiredPreviousTaxFreeLumpSums as string) || ''}
                    onChange={(e) => {
                      onFieldChange('retiredPreviousTaxFreeLumpSums', e.target.value);
                    }}
                    className={inputClass}
                  />
                </div>
              </div>
            </div>
          )}

          {isTransferred && (
            <div className="rounded-xl border border-gray-200 bg-white p-5">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Transfer out details</h4>

              <div className="flex flex-col gap-4">
                <div>
                  <label className={labelClass}>Provider name (transferred to)</label>
                  <input
                    placeholder="Provider name"
                    value={(data.transferredProviderName as string) || ''}
                    onChange={(e) => {
                      onFieldChange('transferredProviderName', e.target.value);
                    }}
                    className={inputClass}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <DateInput
                    label="Date of transfer"
                    value={(data.transferredDateOfTransfer as string) || ''}
                    onChange={(value) => {
                      onFieldChange('transferredDateOfTransfer', value);
                    }}
                  />
                  <div>
                    <label className={labelClass}>Policy reference number (transferred to)</label>
                    <input
                      placeholder="Reference no."
                      value={(data.transferredPolicyReference as string) || ''}
                      onChange={(e) => {
                        onFieldChange('transferredPolicyReference', e.target.value);
                      }}
                      className={inputClass}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>Type of policy (transferred to)</label>
                    <select
                      value={(data.transferredPolicyType as string) || ''}
                      onChange={(e) => {
                        onFieldChange('transferredPolicyType', e.target.value);
                      }}
                      className={inputClass}
                    >
                      {TRANSFERRED_POLICY_TYPE_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.text}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>Type of transfer</label>
                    <select
                      value={(data.transferredTransferType as string) || ''}
                      onChange={(e) => {
                        onFieldChange('transferredTransferType', e.target.value);
                      }}
                      className={inputClass}
                    >
                      {TRANSFER_TYPE_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.text}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <CurrencyInput
                  label="Value as at transfer date"
                  value={data.transferredTransferValue as number | undefined}
                  onChange={(value) => {
                    onFieldChange('transferredTransferValue', value ? parseFloat(value) : undefined);
                  }}
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
